import { useEffect, useRef, useState } from 'react';
import './rank-chart.styles.scss';

const HEIGHT = 200;
const PAD = { top: 12, right: 44, bottom: 28, left: 36 };

// A team's ranking over the season, #1 at the top. history:
// summary/rankHistory. title: "Top 25 ranking" or "Power ranking".
// top25: shade the top 25 (college). regularDays: where the postseason
// starts, if it has.
const RankChart = ({ history, teamId, title, top25, regularDays }) => {

    const boxRef = useRef(null);
    const [ width, setWidth ] = useState(0);
    const [ hover, setHover ] = useState(null);
    const ranks = history?.teams?.[teamId];
    const hasData = Boolean(ranks?.length);

    // Draw at the container's real width (set once the box exists).
    useEffect(() => {
        const el = boxRef.current;
        if (!el) return;
        const observer = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width));
        observer.observe(el);
        return () => observer.disconnect();
    }, [hasData])

    if (!hasData) return null;
    const days = history.days;
    const teamCount = Object.keys(history.teams).length;
    const current = ranks.at(-1);
    const change = ranks.length > 1 ? ranks.at(-2) - current : 0;

    const plotW = Math.max(0, width - PAD.left - PAD.right);
    const plotH = HEIGHT - PAD.top - PAD.bottom;
    const lastDay = Math.max(1, days.at(-1));
    // Zoom to the ranks this team has held (at least the top 25 in
    // college), so moves near the top aren't squashed flat.
    const worst = Math.max(...ranks);
    const roughMax = Math.min(teamCount, Math.max(top25 ? 25 : 10, Math.ceil(worst * 1.15)));
    const step = roughMax <= 30 ? 5 : roughMax <= 60 ? 10 : 25;
    const maxRank = Math.min(teamCount, Math.ceil(roughMax / step) * step);
    const x = (day) => PAD.left + (day / lastDay) * plotW;
    const y = (rank) => PAD.top + ((rank - 1) / Math.max(1, maxRank - 1)) * plotH;
    const yTicks = [1, ...Array.from({ length: Math.floor(maxRank / step) }, (_, i) => (i + 1) * step)
        .filter(t => t > 1 && t <= maxRank)];
    const xTicks = niceDays(lastDay);
    const points = days.map((d, i) => `${x(d).toFixed(1)},${y(ranks[i]).toFixed(1)}`).join(' ');
    const showPost = regularDays && lastDay > regularDays;

    function pick(e) {
        const rect = e.currentTarget.getBoundingClientRect();
        const day = ((e.clientX - rect.left - PAD.left) / plotW) * lastDay;
        let best = 0;
        days.forEach((d, i) => { if (Math.abs(d - day) < Math.abs(days[best] - day)) best = i; });
        setHover(best);
    }

    const h = hover !== null ? { day: days[hover], rank: ranks[hover] } : null;
    const dayLabel = (d) => (d === 0 ? 'Preseason' : `Day ${d}`);

    return (
        <div className='rank-chart'>
            <div className='rank-chart-head'>
                <h3>{title}</h3>
                <div className='rank-now'>
                    <b>#{current}</b>
                    {change !== 0 &&
                        <span className={change > 0 ? 'up' : 'down'}>
                            {change > 0 ? '▲' : '▼'} {Math.abs(change)} since last day
                        </span>
                    }
                </div>
            </div>
            <div className='rank-chart-box' ref={boxRef}>
                {width > 0 &&
                    <svg width={width} height={HEIGHT} role='img'
                        aria-label={`${title}: now ${current} of ${teamCount}, started at ${ranks[0]}`}
                        onPointerMove={pick} onPointerDown={pick} onPointerLeave={() => setHover(null)}>
                        {top25 && maxRank > 25 &&
                            <>
                                <rect className='band' x={PAD.left} y={y(1) - 4} width={plotW} height={y(25) - y(1) + 4} />
                                <text className='band-label' x={PAD.left + 6} y={y(25) - 6}>Top 25</text>
                            </>
                        }
                        {yTicks.map(t => (
                            <g key={t}>
                                <line className='grid' x1={PAD.left} x2={PAD.left + plotW} y1={y(t)} y2={y(t)} />
                                <text className='tick' x={PAD.left - 8} y={y(t) + 4} textAnchor='end'>{t}</text>
                            </g>
                        ))}
                        {xTicks.map(d => (
                            <text key={d} className='tick' x={x(d)} y={HEIGHT - 8} textAnchor='middle'>{d === 0 ? 'Pre' : `Day ${d}`}</text>
                        ))}
                        {showPost &&
                            <>
                                <line className='post-line' x1={x(regularDays)} x2={x(regularDays)} y1={PAD.top} y2={PAD.top + plotH} />
                                <text className='band-label' x={x(regularDays) + 4} y={PAD.top + plotH - 6}>Postseason</text>
                            </>
                        }
                        <polyline className='rank-line' points={points} />
                        <circle className='end-dot' cx={x(days.at(-1))} cy={y(current)} r='4.5' />
                        <text className='end-label' x={x(days.at(-1)) + 9} y={y(current) + 4}>#{current}</text>
                        {h &&
                            <g className='hover'>
                                <line className='crosshair' x1={x(h.day)} x2={x(h.day)} y1={PAD.top} y2={PAD.top + plotH} />
                                <circle className='end-dot' cx={x(h.day)} cy={y(h.rank)} r='4.5' />
                            </g>
                        }
                    </svg>
                }
                {h &&
                    <div className='rank-tooltip' style={{ left: Math.min(Math.max(x(h.day), 60), width - 60), top: Math.max(0, y(h.rank) - 48) }}>
                        <div className='meta'>{dayLabel(h.day)}</div>
                        <b>#{h.rank}</b>
                    </div>
                }
            </div>
            <details className='rank-table'>
                <summary>Show as a table</summary>
                <div className='table-scroll'>
                    <table className='g-table'>
                        <thead><tr><th>Day</th><th className='num'>Rank</th></tr></thead>
                        <tbody>
                            {days.map((d, i) => <tr key={d}><td>{dayLabel(d)}</td><td className='num'>{ranks[i]}</td></tr>).reverse()}
                        </tbody>
                    </table>
                </div>
            </details>
        </div>
    )
}

// A few round day numbers for the x axis.
function niceDays(lastDay) {
    const step = lastDay <= 10 ? 2 : lastDay <= 30 ? 5 : 10;
    const ticks = [0];
    for (let d = step; d < lastDay; d += step) ticks.push(d);
    if (lastDay - ticks.at(-1) >= step / 2) ticks.push(lastDay);
    return ticks;
}

export default RankChart
